import { FC } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents } from '@jointoucan/toucan-design';
import { Alert, AlertTitle, useTheme } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { Trans, useTranslation } from 'next-i18next';

import { useExtensionConfig } from '~/hooks/useExtensionConfig';
import { useMobileDeviceUserAgent } from '~/hooks/useMobileDeviceUserAgent';

const { Button } = ToucanComponents;

interface AllowAccessAlertProps {
  hasGlobalPermissions: boolean;
  onRequestGlobalPermissions: () => Promise<void>;
}

const AllowAccessAlert: FC<AllowAccessAlertProps> = ({ hasGlobalPermissions, onRequestGlobalPermissions }) => {
  const theme = useTheme();
  const isMobileUserAgent = useMobileDeviceUserAgent();
  const { browserName } = useExtensionConfig();
  const { t } = useTranslation('dashboard');

  const isVisible = hasGlobalPermissions !== true && hasGlobalPermissions !== null && !isMobileUserAgent;

  return (
    <Collapse
      in={isVisible && browserName !== 'Safari'}
      css={css`
        margin-bottom: ${isVisible ? theme.spacing(4) : 0};
      `}
    >
      <Alert severity="info">
        <AlertTitle>{t('allowAccessAdditionalPermissionsNeeded')}</AlertTitle>
        {t('allowAccessCheckAnyOpenWindows')}
        <Button
          onClick={onRequestGlobalPermissions}
          type="text"
          css={css`
            margin-top: ${theme.spacing(1)};
          `}
        >
          {t('allowAccess')}
        </Button>
      </Alert>
    </Collapse>
  );
};

export default AllowAccessAlert;

export const FirefoxAllowAccessAlert = () => {
  const theme = useTheme();
  const { t } = useTranslation('dashboard');

  return (
    <Collapse in sx={{ marginBottom: theme.spacing(4) }}>
      <Alert severity="info">
        <AlertTitle>{t('allowAccessAdditionalPermissionsNeeded')}</AlertTitle>
        <Trans
          i18nKey="clickIconInBrowserBar"
          ns="dashboard"
          components={[
            <img
              src="/images/onboarding/extension-icon.svg"
              alt={t('onboardingExtensionIcon')}
              css={css`
                vertical-align: middle;
                overflow: hidden;
                width: 24px;
                height: 24px;
                display: inline-block;
              `}
            />,
          ]}
        />
      </Alert>
    </Collapse>
  );
};
